import {
  FadeUp,
  InputAutosize,
  Select,
  SelectAsync,
} from '@wix/vmaf-adi-components';
import React from 'react';
import { Trans, WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import { AnyAction } from 'redux';

import {
  getADICategories,
  IADICategory,
  SUGGESTED_ADI_CATEGORIES,
} from '../../api/adiCategories/adiCategories';
import CenteredLayout, {
  PageHeader,
} from '../../components/layouts/CenteredLayout';
import NextButton from '../../components/layouts/SplitLayout/Nav/NextButton';
import withPageBI, { IWithPageBIProps } from '../../lib/bi/withPageBI';
import startsWithVowel from '../../lib/startsWithVowel';
import { WizardFlowState } from '../../lib/wizardFlow/types';
import withWizardFlow, {
  IWithWizardFlow,
} from '../../lib/wizardFlow/withWizardFlow';
import * as businessInfoActions from '../../redux/businessInfo/actions';
import { IRootState } from '../../redux/rootReducer';
import * as operations from '../../redux/videoInfo/operations';
import * as selectors from '../../redux/videoInfo/selectors';
import { PersonalVideoPurpose, VideoType } from '../../redux/videoInfo/types';
import { RoutePath } from '../../routing/routes';
import compose from '../../utils/compose';

import { ThunkAction } from 'redux-thunk';
import styles from './Onboarding.scss';

interface IStoreProps {
  videoType?: VideoType;
  videoPurpose: string;
  businessName: string;
  businessCategory?: IADICategory;
  hasPersonalCategory: boolean;
}

interface IActionHandlers {
  onVideoTypeSelect(value: any): void;
  onVideoPurposeSelect(value: any): void;
  onBusinessNameChange(value: any): void;
  onBusinessCategorySelect(value: IADICategory): void;
  setBusinessInfo: any;
}

interface IOnboardingProps
  extends WithTranslation,
    IWithPageBIProps,
    IWithWizardFlow,
    IStoreProps,
    IActionHandlers {}

function mapStateToProps(state: IRootState): IStoreProps {
  return {
    videoType: selectors.getVideoType(state),
    videoPurpose: selectors.getVideoPurpose(state),
    businessName: selectors.getBusinessName(state),
    businessCategory: selectors.getBusinessCategory(state),
    hasPersonalCategory: selectors.hasPersonalCategory(state),
  };
}

function fromHtmlInput(actionCreator: (value: string) => AnyAction) {
  return (event: React.ChangeEvent<HTMLInputElement>): AnyAction =>
    actionCreator(event.target.value);
}

function fromSelect<T>(
  actionCreator: (value?: T) => AnyAction | ThunkAction<any, any, any, any>,
) {
  return (selectedItem) =>
    actionCreator(
      selectedItem && selectedItem.value ? selectedItem.value : null,
    );
}

const mapDispatch: IActionHandlers = {
  onVideoTypeSelect: fromSelect(operations.setVideoType),
  onVideoPurposeSelect: fromSelect(operations.setVideoPurpose),
  onBusinessNameChange: fromHtmlInput(operations.setBusinessName),
  onBusinessCategorySelect: operations.setBusinessCategory,
  setBusinessInfo: businessInfoActions.setBusinessInfo,
};

const VideoTypeLabels: Record<VideoType, string> = {
  [VideoType.BUSINESS]: 'onboarding.video-type.business',
  [VideoType.PERSONAL]: 'onboarding.video-type.personal',
};

const VIDEO_TYPE_OPTIONS = [
  { value: VideoType.BUSINESS },
  { value: VideoType.PERSONAL },
];

const VideoPurposeLabels: Record<PersonalVideoPurpose, string> = {
  [PersonalVideoPurpose.EVENT]: 'onboarding.video-purpose.label.event',
  [PersonalVideoPurpose.ANNOUNCEMENT]:
    'onboarding.video-purpose.label.announcement',
  [PersonalVideoPurpose.MEMORIES]: 'onboarding.video-purpose.label.memories',
  [PersonalVideoPurpose.TRAVELS]: 'onboarding.video-purpose.label.travels',
  [PersonalVideoPurpose.HIGHLIGHTS]:
    'onboarding.video-purpose.label.highlights',
  [PersonalVideoPurpose.OTHER]: 'onboarding.video-purpose.label.other',
};

const VIDEO_PURPOSE_OPTIONS = Object.keys(VideoPurposeLabels).map((value) => ({
  value,
}));

const PAGE_NAME = 'Onboarding';
const FORM_ID = 'onboarding-form';

class Onboarding extends React.Component<IOnboardingProps> {
  private readonly selectorRef: React.RefObject<any> = React.createRef();

  openVideoTypeSelectTimeout: number;

  componentDidMount() {
    this.props.pageBI.pageview();
    this.openVideoTypeSelect();
  }

  componentWillUnmount() {
    window.clearTimeout(this.openVideoTypeSelectTimeout);
  }

  private openVideoTypeSelect() {
    this.openVideoTypeSelectTimeout = window.setTimeout(() => {
      if (!this.props.videoType) {
        this.selectorRef.current.focus();
      }
    }, 600);
  }

  fetchCategories = (query: string) => {
    return getADICategories({
      query,
      sessionId: '1234-1234-12341234',
    });
  };

  isFormValid(): boolean {
    const { videoType } = this.props;
    if (videoType) {
      switch (videoType) {
        case VideoType.BUSINESS:
          const { businessName, businessCategory } = this.props;
          return Boolean(businessName.trim() && businessCategory);
        case VideoType.PERSONAL:
          const { videoPurpose, hasPersonalCategory } = this.props;
          return Boolean(videoPurpose) && hasPersonalCategory;
        default:
          return false;
      }
    }
    return false;
  }

  handleNextClick = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();

    const {
      pageBI,
      videoType,
      videoPurpose,
      businessCategory,
      businessName,
      setBusinessInfo,
    } = this.props;

    pageBI.buttonClick('next', {
      videoType,
      videoPurpose,
      businessCategory,
      businessName,
    });

    setBusinessInfo({
      showClosingScene: videoType === VideoType.BUSINESS,
    });

    this.props.wizard.goNext();
  };

  handleBusinessNameChange = (event) => {
    this.props.onBusinessNameChange(event);
    this.props.setBusinessInfo({ businessName: event.target.value });
  };

  renderBusinessInputs() {
    const { t, businessName, businessCategory, onBusinessCategorySelect } =
      this.props;

    const i18nKey =
      businessCategory && startsWithVowel(businessCategory.displayName)
        ? 'onboarding.business-type-with-vowel'
        : 'onboarding.business-type';

    return (
      <TransitionGroup component="div" className={styles.row}>
        <FadeUp component="span" key={1}>
          <Trans i18nKey={i18nKey}>
            <label htmlFor="business_type"> It's a </label>
            <SelectAsync
              autoFocus
              isClearable
              inputId="business_type"
              value={businessCategory}
              defaultOptions={SUGGESTED_ADI_CATEGORIES}
              defaultOptionsLabel={t('onboarding.business-type-suggestions')}
              loadOptions={this.fetchCategories}
              onChange={onBusinessCategorySelect}
              placeholder={t('onboarding.business-type-placeholder')}
              getOptionValue={(category: IADICategory) => category.displayName}
              getOptionLabel={(category: IADICategory) => category.displayName}
            />
            ,{' '}
          </Trans>
        </FadeUp>
        <FadeUp component="span" key={2} show={Boolean(businessCategory)}>
          <Trans i18nKey="onboarding.business-name">
            and my <label htmlFor="business_name">business name is </label>
            <InputAutosize
              autoFocus
              value={businessName}
              id="business_name"
              name="business_name"
              placeholder={t('onboarding.business-name.placeholder')}
              onChange={this.handleBusinessNameChange}
            />
            .
          </Trans>
        </FadeUp>
      </TransitionGroup>
    );
  }

  renderPersonalInputs() {
    const { t, videoPurpose, onVideoPurposeSelect } = this.props;
    const currentValue = VIDEO_PURPOSE_OPTIONS.find(
      (option) => option.value === videoPurpose,
    );
    return (
      <TransitionGroup component="div" className={styles.row}>
        <FadeUp component="label" htmlFor="video_purpose_input">
          <Trans i18nKey="onboarding.video-purpose">
            It's for{' '}
            <Select
              autoFocus
              isClearable
              inputId="video_purpose_input"
              value={currentValue}
              onChange={onVideoPurposeSelect}
              placeholder={t('onboarding.video-purpose.placeholder')}
              options={VIDEO_PURPOSE_OPTIONS}
              formatOptionLabel={(value: string) =>
                t(VideoPurposeLabels[value])
              }
            />
            .
          </Trans>
        </FadeUp>
      </TransitionGroup>
    );
  }

  render() {
    const { t, videoType } = this.props;
    const currentType = VIDEO_TYPE_OPTIONS.find(
      (item) => item.value === videoType,
    );
    return (
      <CenteredLayout>
        <PageHeader />
        <form
          id={FORM_ID}
          className={styles.form}
          action={RoutePath.MEDIA_IMPORT}
          onSubmit={this.handleNextClick}
        >
          <h3 className={styles.title}>{t('onboarding.page-title')}</h3>
          <TransitionGroup component="div" className={styles.rowNowrap}>
            <FadeUp component="label" htmlFor="video_type_input">
              <Trans i18nKey="onboarding.video-type">
                I want to create a video for{' '}
                <Select
                  innerRef={this.selectorRef}
                  openMenuOnFocus={true}
                  isClearable={false}
                  isSearchable={false}
                  inputId="video_type_input"
                  name="video_type"
                  value={currentType}
                  onChange={this.props.onVideoTypeSelect}
                  options={VIDEO_TYPE_OPTIONS}
                  placeholder={t('onboarding.video-type.placeholder')}
                  formatOptionLabel={(value: VideoType) =>
                    t(VideoTypeLabels[value])
                  }
                />
                .
              </Trans>
            </FadeUp>
          </TransitionGroup>
          {videoType === VideoType.BUSINESS
            ? this.renderBusinessInputs()
            : null}
          {videoType === VideoType.PERSONAL
            ? this.renderPersonalInputs()
            : null}
        </form>
        <footer>
          <NextButton
            className={styles.nextButton}
            form={FORM_ID}
            text={t('onboarding.lets-go')}
            onClick={this.handleNextClick}
            disabled={!this.isFormValid()}
          />
        </footer>
      </CenteredLayout>
    );
  }
}

export default compose(
  withPageBI(PAGE_NAME),
  withWizardFlow(WizardFlowState.ONBOARDING),
  withTranslation(),
  connect(mapStateToProps, mapDispatch),
)(Onboarding);
